import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Play } from 'lucide-react';

interface SkillLevel {
  level: string;
  description: string;
  videoPath?: string;
  color: string;
  badge: string;
}

export function HowWeDoItSection() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  });
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const skillLevels: SkillLevel[] = [
    {
      level: "Starter",
      description: "Master the fundamentals with basic touches and footwork",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Step-over_FutWork_Desktop_U8_U9_U10.mp4",
      color: "green",
      badge: "Level 1"
    },
    {
      level: "Intermediate",
      description: "Advance your technique with step-overs and dynamic ball control",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/L-turns_Desktop_U8_U9_U10_V2.mp4",
      color: "orange",
      badge: "Level 2"
    },
    {
      level: "Strength",
      description: "Build power and explosiveness with targeted strength drills",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Side_Lunges_Right_Strong_Like_a_Pro_Desktop.mp4",
      color: "slate",
      badge: "Specialty"
    },
    {
      level: "Stability",
      description: "Develop balance and core control for better on-field performance",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Ball_Drop_Right_Grab_Quick_Feet_Masters_Desktop.mp4",
      color: "blue",
      badge: "Specialty"
    },
    {
      level: "Speed",
      description: "Enhance quickness and acceleration with high-intensity footwork",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Lightning_Line_Jumps_Blast-Off-Boosters_Desktop.mp4",
      color: "yellow",
      badge: "Specialty"
    }
  ];

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const playNextVideo = useCallback((currentIndex: number) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Pause current video
    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      currentVideo.pause();
      currentVideo.currentTime = 0;
    }

    // Calculate next index (loop back to 0 after last video)
    const nextIndex = (currentIndex + 1) % skillLevels.length;
    const nextVideo = videoRefs.current[nextIndex];
    
    if (nextVideo) {
      nextVideo.currentTime = 0;
      nextVideo.play();
      setActiveVideoIndex(nextIndex);
      
      // Scroll to show the next video
      if (emblaApi) {
        emblaApi.scrollTo(nextIndex);
      }

      // Set timer for 5 seconds before moving to next video
      timerRef.current = setTimeout(() => {
        playNextVideo(nextIndex);
      }, 5000);
    }
  }, [emblaApi, skillLevels.length]);

  const handleVideoClick = (index: number) => {
    // Clear timer when user manually interacts
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Pause all other videos
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    // Play/pause the clicked video
    const clickedVideo = videoRefs.current[index];
    if (clickedVideo) {
      if (clickedVideo.paused) {
        clickedVideo.currentTime = 0;
        clickedVideo.play();
        setActiveVideoIndex(index);
        
        // Restart auto-play sequence from this video
        timerRef.current = setTimeout(() => {
          playNextVideo(index);
        }, 5000);
      } else {
        clickedVideo.pause();
        setActiveVideoIndex(null);
      }
    }
  };

  const handleVideoPlay = (index: number) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setActiveVideoIndex(index);
  };

  const handleVideoPause = (index: number) => {
    if (activeVideoIndex === index) {
      setActiveVideoIndex(null);
    }
  };

  useEffect(() => {
    // Auto-play the first video on mount
    const firstVideo = videoRefs.current[0];
    if (firstVideo) {
      firstVideo.currentTime = 0;
      firstVideo.play();
      setActiveVideoIndex(0);
      
      // Start the 5-second timer
      timerRef.current = setTimeout(() => {
        playNextVideo(0);
      }, 5000);
    }

    return () => {
      // Clean up timer and pause all videos
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      videoRefs.current.forEach(video => {
        if (video) video.pause();
      });
    };
  }, [playNextVideo]);

  const getColorClass = (color: string) => {
    const colors: Record<string, string> = {
      green: 'bg-green-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      slate: 'bg-slate-500',
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-500',
      cyan: 'bg-cyan-500'
    };
    return colors[color] || 'bg-primary';
  };

  return (
    <section className="py-12 md:py-16 bg-muted/30">
      <div className="w-full px-4 md:px-6 lg:px-8">
        {/* Section Title */}
        <h2 className="text-3xl md:text-5xl font-bold italic text-foreground text-center mb-8">
          HOW WE DO IT
        </h2>

        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6 md:gap-8">
            {skillLevels.map((skill, index) => (
              <div
                key={index}
                className="flex-[0_0_90%] sm:flex-[0_0_70%] md:flex-[0_0_55%] lg:flex-[0_0_40%] xl:flex-[0_0_35%] min-w-0"
              >
                <div className="space-y-4">
                  <div className={`relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl group cursor-pointer transition-all duration-300 ${
                    activeVideoIndex === index 
                      ? 'scale-105 ring-4 ring-primary ring-offset-2 ring-offset-background' 
                      : 'hover:scale-105 opacity-90'
                  }`}>
                    {/* Video or Gradient Background */}
                    {skill.videoPath ? (
                      <>
                        <video
                          ref={(el) => (videoRefs.current[index] = el)}
                          src={skill.videoPath}
                          muted
                          playsInline
                          onClick={() => handleVideoClick(index)}
                          onPlay={() => handleVideoPlay(index)}
                          onPause={() => handleVideoPause(index)}
                          className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 cursor-pointer ${
                            activeVideoIndex === index ? 'brightness-110' : 'brightness-100'
                          }`}
                        />
                        
                        {/* Play Icon Overlay */}
                        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
                          activeVideoIndex === index ? 'opacity-0' : 'opacity-100'
                        }`}>
                          <div className="bg-black/50 rounded-full p-4 backdrop-blur-sm">
                            <Play className="h-12 w-12 text-white fill-white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className={`absolute inset-0 bg-gradient-to-br from-${skill.color}-500/30 to-${skill.color}-600/10`} />
                    )}

                    {/* Level Badge */}
                    <div className={`absolute top-4 left-4 px-3 py-1.5 ${getColorClass(skill.color)} rounded-full text-white text-xs font-bold shadow-lg`}>
                      {skill.badge}
                    </div>
                  </div>

                  {/* Text Below Video */}
                  <div className="px-2">
                    <h3 className="text-2xl md:text-3xl font-bold mb-2 text-foreground">
                      {skill.level}
                    </h3>
                    <p className="text-sm md:text-base text-foreground/80">
                      {skill.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`h-3 w-3 rounded-full transition-all ${
                index === selectedIndex 
                  ? 'bg-primary w-10' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
